import { VestingHistory } from '@apeiron/reward'
import {
  AnyObject,
  MayNull,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import useMakePageInfo from '@src/deserialize/hook/useMakePageInfo'
import useMakeResult from '@src/deserialize/hook/useMakeResult'
import useMakeVestingHistory from '@src/deserialize/hook/useMakeVestingHistory'
import graphql from '@src/graphql/query/getVestingHistories'
import { useCallback, useState } from 'react'

const useGetVestingHistories = (): Hook => {
  const [doFetch, { loading, refetch: doRefetch }] = useLazyQuery(graphql)

  const { makeResult } = useMakeResult()

  const { makeVestingHistories } = useMakeVestingHistory()

  const { makePageInfo } = useMakePageInfo()

  const [data, setData] = useState<VestingHistory[]>([])

  const [pageInfo, setPageInfo] = useState<MayNull<PageInfo>>(null)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const { pageInput } = request

      const response = await doFetch({
        variables: {
          input: {
            pagingInput: serializePagingInput(pageInput)
          }
        }
      })

      const result = makeResult<AnyObject>(response)

      const histories = makeVestingHistories(result)

      const pageInfo = makePageInfo(result, pageInput)

      setData(histories)
      setPageInfo(pageInfo)

      return {
        histories,
        pageInfo
      }
    },
    [doFetch, makePageInfo, makeResult, makeVestingHistories]
  )

  const refetch = useCallback(() => {
    doRefetch()
  }, [doRefetch])

  return {
    data,
    loading,
    pageInfo,
    fetch,
    refetch
  }
}

type Request = {
  pageInput: PageInput
}

type Response = {
  histories: VestingHistory[]
  pageInfo: MayNull<PageInfo>
}

type Hook = {
  data: VestingHistory[]
  loading: boolean
  pageInfo: MayNull<PageInfo>
  fetch: (request: Request) => Promise<Response>
  refetch: () => void
}

export default useGetVestingHistories
